// @figma file=pkCGby54zXzTsccXvHqzO6 node=3884:83880 name="File Input" set="mytaible-shadcn-studio" verified=2026-03-22

import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../../../lib/utils';

// ─── Variants ─────────────────────────────────────────────────────────────────

/**
 * CVA for the trigger button — primary fill, left-rounded corners only.
 * Figma: Solid Button (style=primary, shape=rounded) inside the File Input
 * component set. `borderRadius: 10px 0px 0px 10px` → `rounded-l-[var(--radius)]`.
 */
const fileInputButtonVariants = cva(
  [
    'inline-flex shrink-0 items-center justify-center whitespace-nowrap font-medium',
    // ── Figma: fill_9S2W25 (bg-primary) + fill_LG168T (text-primary-foreground) ─
    'bg-primary text-primary-foreground',
    // ── Figma: stroke_EZAX6G (primary border) ────────────────────────────────────
    'border border-primary',
    // ── Figma: borderRadius 10px 0 0 10px → left corners only ───────────────────
    'rounded-l-[var(--radius)] rounded-r-none',
    // ── Hover — darkened primary ─────────────────────────────────────────────────
    'hover:bg-primary/90',
    // ── Focus ring ───────────────────────────────────────────────────────────────
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
    'focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    // ── Animate ──────────────────────────────────────────────────────────────────
    'transition-colors',
  ].join(' '),
  {
    variants: {
      /**
       * Maps Figma `size` dimension.
       * sm → h-8 · md → h-9 · lg → h-10
       */
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-9 px-4 text-sm',
        lg: 'h-10 px-6 text-sm',
      },
    },
    defaultVariants: { size: 'md' },
  },
);

/**
 * CVA for the filename display area — right-rounded corners only.
 * Figma: Default Input (State=Default, Shape=Rounded) inside the File Input
 * component set. `borderRadius: 0 8px 8px 0` → `rounded-r-[var(--radius)]`.
 * `border-l-0` avoids a doubled border where the button and display meet.
 */
const fileInputDisplayVariants = cva(
  [
    'flex flex-1 items-center overflow-hidden',
    // ── Figma: fill_4P148K (bg-background) + stroke_US95ZD (border-input) ────────
    'border border-input border-l-0 bg-background',
    // ── Figma: borderRadius 0 8px 8px 0 → right corners only ────────────────────
    'rounded-l-none rounded-r-[var(--radius)]',
    // ── Figma: effects Light/box-shadow/primary/xs ────────────────────────────────
    'shadow-sm',
  ].join(' '),
  {
    variants: {
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-9 px-3 text-sm',
        lg: 'h-10 px-4 text-sm',
      },
    },
    defaultVariants: { size: 'md' },
  },
);

// ─── Props ────────────────────────────────────────────────────────────────────

export interface FileInputProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>,
    VariantProps<typeof fileInputButtonVariants> {
  /**
   * MIME type filter forwarded to the native file input.
   * Examples: `"image/*"`, `".pdf,.doc"`, `"*\/*"` (default — all files).
   */
  accept?: string;
  /**
   * Allow selecting multiple files simultaneously.
   * When multiple files are selected the display area shows "{n} files selected".
   * Figma: maps to `multiple` prop in the intake spec.
   */
  multiple?: boolean;
  /**
   * Disables both the button trigger and drag-and-drop zone.
   * Figma: `isDisabled=True` — opacity 0.5 applied to the whole component,
   * pointer events blocked.
   */
  disabled?: boolean;
  /**
   * Callback fired when the file selection changes.
   * Receives the `FileList` from the change event or drag-drop, or `null`
   * when the selection is cleared.
   */
  onChange?: (files: FileList | null) => void;
  /**
   * Content rendered inside the trigger button.
   * Defaults to `"Choose File"`.
   * Figma: Solid Button `¶ label` component property.
   */
  children?: React.ReactNode;
  /** Additional CSS classes merged onto the root wrapper element. */
  className?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * File upload control that composes a primary trigger button (left) and a
 * styled filename display area (right). A visually hidden native
 * `<input type="file">` is activated when the button is clicked or files are
 * dropped onto the component.
 *
 * Supports drag-and-drop: dragging files over the component shows a ring
 * indicator and triggers `onChange` on drop — no dialog is opened.
 *
 * Figma: `pkCGby54zXzTsccXvHqzO6` node `3884:83880` — component set
 * `414:13265`. Sizes: sm / md / lg. States: empty (File added=False),
 * file-selected (File added=True), disabled (isDisabled=True).
 *
 * @example
 * // Basic — fires onChange with the selected FileList
 * <FileInput onChange={(files) => console.log(files)} />
 *
 * @example
 * // Custom label, image-only filter, multiple selection
 * <FileInput accept="image/*" multiple onChange={handleFiles}>
 *   Upload Images
 * </FileInput>
 *
 * @example
 * // Disabled state
 * <FileInput disabled />
 *
 * @example
 * // Large size with label association
 * <label htmlFor="attachment">Attachment</label>
 * <FileInput id="attachment" size="lg" onChange={handleFiles} />
 */
const FileInput = React.forwardRef<HTMLInputElement, FileInputProps>(
  (
    {
      className,
      size = 'md',
      accept,
      multiple = false,
      disabled = false,
      onChange,
      children,
      ...props
    },
    ref,
  ) => {
    const [fileName, setFileName] = React.useState('');
    const [isDragging, setIsDragging] = React.useState(false);
    const internalRef = React.useRef<HTMLInputElement>(null);

    // Expose the hidden <input type="file"> to the forwarded ref so callers
    // can imperatively invoke .click() or read .files.
    React.useImperativeHandle(ref, () => internalRef.current as HTMLInputElement, []);

    const handleFiles = React.useCallback(
      (files: FileList | null) => {
        if (!files || files.length === 0) {
          setFileName('');
          onChange?.(null);
          return;
        }
        // Figma: "File added=True" shows the filename in the display area.
        // Multiple files: show a summary count instead.
        setFileName(files.length === 1 ? files[0].name : `${files.length} files selected`);
        onChange?.(files);
      },
      [onChange],
    );

    // ── Drag-and-drop ──────────────────────────────────────────────────────────

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      handleFiles(e.dataTransfer.files);
    };

    return (
      // biome-ignore lint/a11y/useSemanticElements: file drop zone requires div for DragEvent compatibility
      <div
        role="group"
        aria-disabled={disabled || undefined}
        className={cn(
          // ── Layout ────────────────────────────────────────────────────────────
          'flex w-full items-stretch',
          // ── Outer radius for consistent drag-ring clipping ────────────────────
          'rounded-[var(--field-radius,var(--radius))]',
          // ── Drag-over state — ring-2 ring-ring matches focus ring convention ──
          isDragging && 'ring-2 ring-ring ring-offset-2 ring-offset-background',
          // ── Disabled — Figma: opacity 0.5, pointer-events blocked ─────────────
          disabled && 'pointer-events-none opacity-50',
          className,
        )}
        onDragOver={!disabled ? handleDragOver : undefined}
        onDragLeave={!disabled ? handleDragLeave : undefined}
        onDrop={!disabled ? handleDrop : undefined}
        {...props}
      >
        {/* Visually hidden native file input — activated by button click or drop */}
        <input
          ref={internalRef}
          type="file"
          className="sr-only"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          tabIndex={-1}
          aria-hidden="true"
          onChange={(e) => {
            if (!disabled) handleFiles(e.target.files);
          }}
        />

        {/* Trigger button — Figma: Solid Button, primary style, left-rounded */}
        <button
          type="button"
          className={fileInputButtonVariants({ size })}
          onClick={() => internalRef.current?.click()}
          disabled={disabled}
        >
          {children ?? 'Choose File'}
        </button>

        {/* Filename display area — Figma: Default Input, right-rounded corners */}
        <div
          className={cn(
            fileInputDisplayVariants({ size }),
            // ── Placeholder colour when no file is selected ───────────────────────
            // Figma: fill_N5XLSA (muted-foreground) for placeholder text
            !fileName ? 'text-muted-foreground' : 'text-foreground',
          )}
          aria-live="polite"
          aria-atomic="true"
        >
          <span className="truncate">
            {isDragging ? 'Drop files here…' : fileName || 'No file chosen'}
          </span>
        </div>
      </div>
    );
  },
);
FileInput.displayName = 'FileInput';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { FileInput, fileInputButtonVariants, fileInputDisplayVariants };
